import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { Building2, BarChart2, Network, HeartHandshakeIcon, SparklesIcon } from 'lucide-react';
import { cn } from '../lib/utils';
interface WhatWeDoFeatureProps {
  className?: string;
  circleText?: string;
  badgeTexts?: {
    first: string;
    second: string;
    third: string;
  };
  buttonTexts?: {
    first: string;
    second: string;
  };
  title?: string;
  description?: string;
  lightColor?: string;
}
const WhatWeDoFeature = ({
  className,
  circleText,
  badgeTexts,
  buttonTexts,
  title,
  description,
  lightColor
}: WhatWeDoFeatureProps) => {
  return <div className={cn('relative flex h-[250px] sm:h-[300px] w-full max-w-[800px] flex-col items-center', className)} data-id="element-270">
      {/* SVG Paths */}
      <svg className="h-full w-full text-muted" width="100%" height="100%" viewBox="0 0 200 100" data-id="element-271">
        <defs data-id="element-272">
          <linearGradient id="pathGradient" gradientUnits="userSpaceOnUse" data-id="element-273">
            <stop offset="0%" stopColor="rgba(37, 99, 235, 0)" data-id="element-274" />
            <stop offset="50%" stopColor="rgba(37, 99, 235, 1)" data-id="element-275" />
            <stop offset="100%" stopColor="rgba(37, 99, 235, 0)" data-id="element-276" />
            <animate attributeName="x1" values="-100%;0%;100%" dur="2s" repeatCount="indefinite" data-id="element-277" />
            <animate attributeName="x2" values="0%;100%;200%" dur="2s" repeatCount="indefinite" data-id="element-278" />
          </linearGradient>
          {/* Masks and gradients */}
          <mask id="db-mask-1" data-id="element-279">
            <path d="M 31 10 v 15 q 0 5 5 5 h 59 q 5 0 5 5 v 10" strokeWidth="0.5" stroke="white" data-id="element-280" />
          </mask>
          <mask id="db-mask-2" data-id="element-281">
            <path d="M 100 10 v 35" strokeWidth="0.5" stroke="white" data-id="element-282" />
          </mask>
          <mask id="db-mask-3" data-id="element-283">
            <path d="M 170 10 v 15 q 0 5 -5 5 h -60 q -5 0 -5 5 v 10" strokeWidth="0.5" stroke="white" data-id="element-284" />
          </mask>
          <radialGradient id="db-blue-grad" fx="1" data-id="element-285">
            <stop offset="0%" stopColor={lightColor || '#2563eb'} data-id="element-286" />
            <stop offset="100%" stopColor="transparent" data-id="element-287" />
          </radialGradient>
        </defs>
        {/* Base paths */}
        <g stroke="rgba(255,255,255,0.1)" fill="none" strokeWidth="0.4" data-id="element-288">
          <path d="M 31 10 v 15 q 0 5 5 5 h 59 q 5 0 5 5 v 10" data-id="element-289" />
          <path d="M 100 10 v 35" data-id="element-290" />
          <path d="M 170 10 v 15 q 0 5 -5 5 h -60 q -5 0 -5 5 v 10" data-id="element-291" />
        </g>
        {/* Animated paths */}
        <g stroke="url(#pathGradient)" fill="none" strokeWidth="0.4" data-id="element-292">
          <path d="M 31 10 v 15 q 0 5 5 5 h 59 q 5 0 5 5 v 10" data-id="element-293" />
          <path d="M 100 10 v 35" data-id="element-294" />
          <path d="M 170 10 v 15 q 0 5 -5 5 h -60 q -5 0 -5 5 v 10" data-id="element-295" />
        </g>
        {/* Blue Lights */}
        <g mask="url(#db-mask-1)" data-id="element-296">
          <circle className="database db-light-1" cx="0" cy="0" r="12" fill="url(#db-blue-grad)" data-id="element-297" />
        </g>
        <g mask="url(#db-mask-2)" data-id="element-298">
          <circle className="database db-light-2" cx="0" cy="0" r="12" fill="url(#db-blue-grad)" data-id="element-299" />
        </g>
        <g mask="url(#db-mask-3)" data-id="element-300">
          <circle className="database db-light-3" cx="0" cy="0" r="12" fill="url(#db-blue-grad)" data-id="element-301" />
        </g>
        {/* Buttons */}
        <g stroke="currentColor" fill="none" strokeWidth="0.4" data-id="element-302">
          {/* First Button */}
          <g data-id="element-303">
            <rect fill="#18181B" x="14" y="5" width="37" height="10" rx="5" data-id="element-304"></rect>
            <FeatureIcon x="18" y="7.5" icon="acquire" data-id="element-305"></FeatureIcon>
            <text x="28" y="12" fill="white" stroke="none" fontSize="5" fontWeight="500" data-id="element-306">
              {badgeTexts?.first || 'Acquire'}
            </text>
          </g>
          {/* Second Button */}
          <g data-id="element-307">
            <rect fill="#18181B" x="83" y="5" width="45" height="10" rx="5" data-id="element-308"></rect>
            <FeatureIcon x="87" y="7.5" icon="consolidate" data-id="element-309"></FeatureIcon>
            <text x="97" y="12" fill="white" stroke="none" fontSize="5" fontWeight="500" data-id="element-310">
              {badgeTexts?.second || 'Consolidate'}
            </text>
          </g>
          {/* Third Button */}
          <g data-id="element-311">
            <rect fill="#18181B" x="150" y="5" width="40" height="10" rx="5" data-id="element-312"></rect>
            <FeatureIcon x="154" y="7.5" icon="integrate" data-id="element-313"></FeatureIcon>
            <text x="164" y="12" fill="white" stroke="none" fontSize="5" fontWeight="500" data-id="element-314">
              {badgeTexts?.third || 'Integrate'}
            </text>
          </g>
        </g>
      </svg>
      {/* Main Box */}
      <div className="absolute bottom-0 sm:bottom-10 flex w-full flex-col items-center" data-id="element-315">
        {/* box title */}
        <div className="absolute -top-3 z-20 flex items-center justify-center rounded-lg border border-gray-700 bg-gray-900 px-2 py-1 sm:-top-4 sm:py-1.5" data-id="element-316">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/uYkAKCz9MEFJTpGQ4cKVHd/ISRAELCIVILIANTRAININGBLUEANDWHITE.png" alt="Israeli Civilian Training Foundation" className="h-4 sm:h-5 w-auto object-contain" data-id="element-317" />
        </div>
        {/* box outer circle */}
        <div className="absolute -bottom-4 sm:-bottom-8 z-30 grid h-[40px] w-[40px] sm:h-[60px] sm:w-[60px] place-items-center rounded-full border-t border-gray-700 bg-gray-900" data-id="element-318"></div>
        {/* box content */}
        <div className="relative z-10 flex h-[120px] sm:h-[150px] w-full items-center justify-center overflow-hidden rounded-lg border border-gray-700 bg-gray-900 shadow-md" data-id="element-319">
          {/* Feature Icons */}
          <div className="relative w-full h-full flex items-center justify-center" data-id="element-320">
            {/* Left Side Icons */}
            <div className="absolute left-2 sm:left-8 h-full flex flex-col justify-center gap-4 sm:gap-6" data-id="element-321">
              <div className="flex items-center gap-2 sm:gap-3" data-id="element-322">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-blue-400/20 flex items-center justify-center" data-id="element-323">
                  <Building2 className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400" data-id="element-324" />
                </div>
                <p className="text-[8px] sm:text-[10px] text-blue-400/80 font-light whitespace-nowrap" data-id="element-325">
                  Professional Management
                </p>
              </div>
              <div className="flex items-center gap-2 sm:gap-3" data-id="element-326">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-blue-400/20 flex items-center justify-center" data-id="element-327">
                  <BarChart2 className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400" data-id="element-328" />
                </div>
                <p className="text-[8px] sm:text-[10px] text-blue-400/80 font-light whitespace-nowrap" data-id="element-329">
                  Industry Expertise
                </p>
              </div>
            </div>
            {/* Center Icon */}
            <div className="flex flex-col items-center justify-center relative z-20" data-id="element-330">
              <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-full bg-blue-400 flex items-center justify-center" data-id="element-331">
                <HeartHandshakeIcon className="w-5 h-5 sm:w-7 sm:h-7 text-white" data-id="element-332" />
              </div>
              <p className="mt-2 text-[10px] sm:text-xs text-blue-400 font-light" data-id="element-333">
                Patient Care
              </p>
            </div>
            {/* Right Side Icons */}
            <div className="absolute right-2 sm:right-8 h-full flex flex-col justify-center gap-4 sm:gap-6" data-id="element-334">
              <div className="flex items-center justify-end gap-2 sm:gap-3" data-id="element-335">
                <p className="text-[8px] sm:text-[10px] text-blue-400/80 font-light whitespace-nowrap" data-id="element-336">
                  Systems & Processes
                </p>
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-blue-400/20 flex items-center justify-center" data-id="element-337">
                  <Network className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400" data-id="element-338" />
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 sm:gap-3" data-id="element-339">
                <p className="text-[8px] sm:text-[10px] text-blue-400/80 font-light whitespace-nowrap" data-id="element-340">
                  Service Excellence
                </p>
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-blue-400/20 flex items-center justify-center" data-id="element-341">
                  <SparklesIcon className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400" data-id="element-342" />
                </div>
              </div>
            </div>
          </div>
          {/* Background Circles */}
          <motion.div className="absolute -bottom-14 h-[100px] w-[100px] rounded-full border-t border-gray-700 bg-blue-900/10" animate={{
          scale: [0.98, 1.02, 0.98, 1, 1, 1, 1, 1, 1]
        }} transition={{
          duration: 2,
          repeat: Infinity
        }} data-id="element-343" />
          <motion.div className="absolute -bottom-20 h-[145px] w-[145px] rounded-full border-t border-gray-700 bg-blue-900/10" animate={{
          scale: [1, 1, 1, 0.98, 1.02, 0.98, 1, 1, 1]
        }} transition={{
          duration: 2,
          repeat: Infinity
        }} data-id="element-344" />
          <motion.div className="absolute -bottom-[100px] h-[190px] w-[190px] rounded-full border-t border-gray-700 bg-blue-900/10" animate={{
          scale: [1, 1, 1, 1, 1, 0.98, 1.02, 0.98, 1, 1]
        }} transition={{
          duration: 2,
          repeat: Infinity
        }} data-id="element-345" />
          <motion.div className="absolute -bottom-[120px] h-[235px] w-[235px] rounded-full border-t border-gray-700 bg-blue-900/10" animate={{
          scale: [1, 1, 1, 1, 1, 1, 0.98, 1.02, 0.98, 1]
        }} transition={{
          duration: 2,
          repeat: Infinity
        }} data-id="element-346" />
        </div>
      </div>
    </div>;
};
export default WhatWeDoFeature;
const FeatureIcon = ({
  x = '0',
  y = '0',
  icon
}: {
  x: string;
  y: string;
  icon: string;
}) => {
  return <svg x={x} y={y} xmlns="http://www.w3.org/2000/svg" width="5" height="5" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-347">
      {icon === 'acquire' && <>
          <rect x="4" y="4" width="16" height="16" rx="2" ry="2" data-id="element-348" />
          <path d="M9 15V9h6" data-id="element-349" />
          <path d="m9 9 6 6" data-id="element-350" />
        </>}
      {icon === 'consolidate' && <>
          <path d="M3 3v18h18" data-id="element-351" />
          <path d="m3 10 8-5 8 3 5-3" data-id="element-352" />
          <path d="m3 17 8-5 8 3 5-4" data-id="element-353" />
        </>}
      {icon === 'integrate' && <>
          <path d="M2 12h20" data-id="element-354" />
          <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" data-id="element-355" />
          <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" data-id="element-356" />
          <path d="M6 15H4.5a2.5 2.5 0 0 0 0 5H6" data-id="element-357" />
          <path d="M18 15h1.5a2.5 2.5 0 0 1 0 5H18" data-id="element-358" />
        </>}
    </svg>;
};